import { replySubjectFor, type Attachment } from "@catmail/shared";
import { useQuery } from "@tanstack/react-query";
import { Menu, MoreHorizontal, Paperclip, SearchX, WifiOff } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useNavigate } from "react-router";

import {
  Button,
  EmptyState,
  IconButton,
  NavDrawer,
  ResizeHandle,
  Snackbar,
} from "@/components";
import { contactsApi, messagesApi } from "@/features/mail/api/client";
import {
  ChatHeader,
  AddAddressDialog,
  AvatarDialog,
  ChatTimeline,
  Composer,
  EmptyChat,
  HtmlMailViewer,
  ManageAddressDialog,
  MobileNav,
  NewChatDialog,
  PasswordDialog,
  PeerProfileDialog,
  RenameDialog,
  RoomFilesDialog,
  RoomListPanel,
  RoomRail,
} from "@/features/mail/components";
import { useMailWorkspace } from "@/features/mail/hooks/useMailWorkspace";
import { usePushNotifications } from "@/features/mail/hooks/usePushNotifications";
import { NARROW_QUERY, useMediaQuery } from "@/lib/useMediaQuery";
import { usePanelWidths } from "@/features/mail/hooks/usePanelWidths";
import {
  canReplyTo,
  composerPlaceholderFor,
  filesIn,
  peersOf,
} from "@/features/mail/lib/roomView";

/**
 * 첨부를 내려받는다.
 *
 * 서버가 Content-Disposition을 붙여 주므로 링크를 누르기만 하면 된다.
 * fetch로 받아 Blob을 만들지 않는 이유: 큰 파일이 통째로 메모리에 올라가고,
 * 브라우저 기본 다운로드 UI(진행률·이어받기)를 잃는다.
 */
function downloadAttachment(attachment: Attachment): void {
  // 내용을 못 받은 첨부는 내려받을 것이 없다.
  if (!attachment.downloadUrl) return;

  const link = document.createElement("a");
  link.href = attachment.downloadUrl;
  link.download = attachment.filename;
  document.body.append(link);
  link.click();
  link.remove();
}

export function MailPage() {
  const navigate = useNavigate();
  const workspace = useMailWorkspace();
  const { selectedRoom, htmlMessage } = workspace;
  const { widths, limits, setWidth, resetWidth } = usePanelWidths();

  /** 서랍이 도는 구간인가. 배치는 CSS가 가르고, 여기서는 문구 길이에만 쓴다. */
  const narrow = useMediaQuery(NARROW_QUERY);

  /**
   * 좁은 화면에서 방 목록 서랍이 열려 있는가. 넓은 화면에서는 쓰이지 않는다.
   *
   * 주소에 얹지 않는다. 이건 "지금 무엇을 보고 있는가"가 아니라 그 위에 잠깐
   * 덮인 것이고, 남에게 줄 링크에 따라갈 이유도 없다. 방을 고르지 않은
   * 상태(`/`)로 들어오면 고를 것이 화면에 있어야 하므로 열린 채로 시작한다.
   */
  const [navOpen, setNavOpen] = useState(workspace.routeRoomId === null);

  /**
   * 승인 화면으로 가는 길. 넓은 화면과 좁은 화면이 **같은 값을 받아야** 한다.
   *
   * 최고 관리자가 아니면 `undefined`다. 그 줄을 흐리게 그리는 대신 아예 안
   * 그린다 — 남에게 있다는 것조차 알리지 않는다. 서버도 같은 이유로 403이 아니라
   * 404를 준다(routes/admin.ts).
   */
  const openAdmin = workspace.isAdmin ? () => void navigate("/admin") : undefined;

  /**
   * 이 기기 알림.
   *
   * 로그인한 뒤에만 물어본다 — 구독은 사람에게 묶이므로 누구인지 모르는 채로
   * 등록할 곳이 없다. 넓은 화면과 좁은 화면이 **같은 값을 받아야** 한다.
   */
  const push = usePushNotifications(workspace.user.id.length > 0);
  const pushControl = {
    available: push.available,
    state: push.state,
    onTurnOn: () => void push.turnOn(),
    onTurnOff: () => void push.turnOff(),
  };

  /**
   * 주소가 바뀌면 서랍도 따라간다.
   *
   * 방을 닫고 목록으로 돌아오면(`/`) 고를 것이 화면에 있어야 하고, 방이 열리면
   * 그 대화가 보여야 한다. 방으로 가는 길이 여럿이라(목록에서 고르기, 새 대화
   * 시작, 프로필의 "채팅하기", 남이 준 링크) 길목마다 따로 여닫는 대신 주소가
   * 바뀌는 이 한 곳에서 맞춘다.
   */
  useEffect(() => {
    setNavOpen(workspace.routeRoomId === null);
  }, [workspace.routeRoomId]);

  // 원문 HTML은 07 화면을 열 때만 가져온다.
  const htmlQuery = useQuery({
    queryKey: ["message-html", htmlMessage?.id],
    queryFn: () => messagesApi.html(htmlMessage?.id ?? ""),
    enabled: htmlMessage !== null,
  });

  // 새 채팅 모달을 열 때만 주소록을 읽는다. 상대 목록은 **주소마다 따로**라
  // 지금 계정을 같이 넘긴다 — 안 넘기면 업무 주소에서 새 대화를 시작하는데
  // 개인 주소로 주고받은 사람이 후보로 뜬다.
  const contactsQuery = useQuery({
    queryKey: ["contacts", workspace.identity?.id ?? null],
    queryFn: () => contactsApi.search("", workspace.identity?.id ?? null),
    enabled: workspace.newChatOpen,
  });

  if (workspace.isUnauthenticated) {
    return <Navigate to="/login" replace />;
  }

  // 승인 전에는 메일함이 열리지 않는다. 서버도 막지만(requireApproved) 화면이
  // 먼저 돌려보내야 빈 목록에 오류만 뜨는 상태를 안 본다.
  if (workspace.needsApproval) {
    return <Navigate to="/pending" replace />;
  }

  // 원문 주소인데 그 메일을 못 찾으면 대화로 돌려보낸다.
  if (workspace.htmlMissing && selectedRoom) {
    return <Navigate to={`/r/${selectedRoom.id}`} replace />;
  }

  const replyable = selectedRoom ? canReplyTo(selectedRoom) : false;

  // 그냥 보내면 붙을 제목. 서버도 같은 규칙을 쓴다(routes/messages.ts).
  // 미리 보여줘야 "내가 뭘 보내는지"를 알고 고칠 수 있다.
  const lastSubject = workspace.messages.at(-1)?.subject;
  const replySubject = lastSubject ? replySubjectFor(lastSubject) : undefined;

  /**
   * 대화창 자리에 무엇을 놓을지.
   *
   * 순서가 곧 우선순위다. **못 부르는 것 → 못 찾은 것 → 원문 → 대화 → 빈 화면.**
   * 빈 화면이 맨 끝인 이유는, 앞의 어떤 사정도 "고를 대화가 없다"로 뭉뚱그리면
   * 사용자가 무엇이 잘못됐는지 알 수 없기 때문이다.
   */
  function mainPane(): ReactNode {
    if (workspace.loadFailed) {
      return (
        <EmptyState
          size="md"
          icon={<WifiOff />}
          title="서버에 닿지 못했어요"
          description="인터넷이 끊겼거나 서버가 잠깐 쉬는 중일 수 있어요. 받은 메일은 그대로 있으니 다시 불러오면 돼요."
          actions={
            <Button size="sm" onClick={workspace.retryLoad}>
              다시 불러오기
            </Button>
          }
        />
      );
    }

    if (workspace.roomNotFound) {
      return (
        <EmptyState
          size="md"
          icon={<SearchX />}
          title="그 대화를 찾을 수 없어요"
          description="링크가 가리키던 대화가 사라졌거나, 내 것이 아니에요."
          actions={
            <Button size="sm" onClick={workspace.goHome}>
              전체 대화 보기
            </Button>
          }
        />
      );
    }

    if (htmlMessage && selectedRoom) {
      return (
        <HtmlMailViewer
          message={htmlMessage}
          sender={
            peersOf(selectedRoom).find((peer) => peer.id === htmlMessage.senderId) ??
            null
          }
          html={
            htmlQuery.data?.html ??
            (htmlQuery.isLoading
              ? "<p style='font-family:sans-serif;color:#888'>불러오는 중…</p>"
              : "<p style='font-family:sans-serif;color:#888'>원문을 불러오지 못했어요</p>")
          }
          onClose={workspace.closeHtml}
        />
      );
    }

    if (selectedRoom) {
      return (
        <>
          <ChatHeader
            room={selectedRoom}
            subject={workspace.messages.at(-1)?.subject}
            onOpenNav={() => setNavOpen(true)}
            onOpenProfile={() => workspace.openProfile(selectedRoom.id)}
            actions={
              selectedRoom.isQuiet ? (
                <Button
                  variant="outline"
                  size="pill"
                  onClick={() => workspace.toggleQuiet(selectedRoom.id)}
                >
                  대화 목록으로 옮기기
                </Button>
              ) : (
                <>
                  <IconButton label="주고받은 파일" onClick={workspace.openFiles}>
                    <Paperclip />
                  </IconButton>
                  <IconButton
                    label="이 사람 정보"
                    onClick={() => workspace.openProfile(selectedRoom.id)}
                  >
                    <MoreHorizontal />
                  </IconButton>
                </>
              )
            }
          />

          <ChatTimeline
            room={selectedRoom}
            messages={workspace.messages}
            loading={workspace.isLoadingMessages}
            // 조용한 방이 하는 일은 둘이다 — 목록 앞자리를 차지하지 않고,
            // 알림도 울리지 않는다(apps/api/src/events/push.ts). 둘 다 적는다.
            // 하나만 적으면 나머지 하나를 기대하게 된다.
            notice={
              selectedRoom.isQuiet
                ? "조용한 방이에요. 새 메일이 와도 목록에 끼어들지 않고 알림도 울리지 않아요"
                : null
            }
            onOpenHtml={workspace.openHtml}
            onOpenAttachment={downloadAttachment}
            onOpenProfile={(sender) =>
              workspace.openProfile(selectedRoom.id, sender.id)
            }
            onRetry={workspace.retryMessage}
            onLoadOlder={workspace.loadOlderMessages}
            hasOlder={workspace.hasOlderMessages}
            loadingOlder={workspace.isLoadingOlder}
          />

          {/*
            key로 방마다 다른 인스턴스를 만든다. 고르지 않으면 고른 첨부와
            고쳐 쓰던 제목이 다음 방으로 따라간다 — 본문은 방별로 보관되지만
            (workspace.draft) 나머지는 입력창 안에 있다.
          */}
          <Composer
            key={selectedRoom.id}
            disabled={!replyable}
            placeholder={composerPlaceholderFor(selectedRoom, narrow)}
            defaultSubject={replySubject}
            value={workspace.draft}
            onValueChange={workspace.setDraft}
            onSend={workspace.sendMessage}
            onNotice={workspace.notify}
          />
        </>
      );
    }

    // 방 목록이 아직 오지 않았으면 "대화가 없다"고 단정하지 않는다.
    if (workspace.isLoadingRooms) return null;

    return (
      <EmptyChat
        // 처음 온 사람인지는 **"전체"가 비었을 때만** 알 수 있다. 좁혀 보는
        // 탭이나 검색이 비었다고 앱을 처음 여는 사람인 것은 아니다.
        firstTime={
          workspace.filter === "all" &&
          workspace.query.length === 0 &&
          workspace.rooms.length === 0
        }
        onNewChat={workspace.openNewChat}
      />
    );
  }

  return (
    <div className="flex h-full">
      {/*
        좁은 화면에서 레일과 목록은 **서랍 안**에 들어간다. 대화는 늘 바탕에
        남아 있고 서랍이 그 위를 덮는다 — 디스코드 모바일과 같다.
        넓은 화면에서는 서랍이 아무 일도 하지 않고 그냥 왼쪽 두 칸이 된다.
      */}
      <NavDrawer open={navOpen} onOpenChange={setNavOpen}>
        <RoomRail
          className="hidden md:flex"
          user={workspace.user}
          filter={workspace.filter}
          counts={workspace.counts}
          onFilterChange={workspace.setFilter}
          onNewChat={workspace.openNewChat}
          onSignOut={workspace.signOut}
          onChangeReceiveMode={workspace.changeReceiveMode}
          onRename={workspace.openRenameSelf}
          onChangeAvatar={workspace.openAvatar}
          onChangePassword={workspace.openPassword}
          onAddAddress={workspace.openAddAddress}
          onOpenAddress={workspace.openAddress}
          currentIdentityId={workspace.identity?.id ?? null}
          // 레일 머리에 적힌다. 주소가 하나뿐이어도 적는다 — 갈아탈 곳이
          // 없다는 것과 지금 무엇으로 쓰고 있는지는 다른 이야기다.
          address={workspace.identity?.address ?? null}
          unreadByIdentity={workspace.unreadByIdentity}
          onSelectIdentity={workspace.selectIdentity}
          onOpenAdmin={openAdmin}
          push={pushControl}
          width={widths.rail}
        />

        {/* 끌 수 있는 칸 자체가 없는 화면에서는 손잡이도 없다 */}
        <ResizeHandle
          className="hidden md:flex"
          label="분류 레일 폭"
          value={widths.rail}
          min={limits.rail.min}
          max={limits.rail.max}
          onChange={(next) => setWidth("rail", next)}
          onReset={() => resetWidth("rail")}
        />

        <RoomListPanel
          // 지금 쓰는 주소. 좁은 화면에는 레일이 없어서 여기가 유일한 자리다.
          address={workspace.identity?.address ?? null}
          nav={
            <MobileNav
              className="md:hidden"
              user={workspace.user}
              filter={workspace.filter}
              counts={workspace.counts}
              onFilterChange={workspace.setFilter}
              onSignOut={workspace.signOut}
              onChangeReceiveMode={workspace.changeReceiveMode}
              onRename={workspace.openRenameSelf}
              onChangeAvatar={workspace.openAvatar}
              onChangePassword={workspace.openPassword}
              onAddAddress={workspace.openAddAddress}
              onOpenAddress={workspace.openAddress}
              currentIdentityId={workspace.identity?.id ?? null}
              unreadByIdentity={workspace.unreadByIdentity}
              onSelectIdentity={workspace.selectIdentity}
              // 넓은 화면에만 넘기고 여기를 빠뜨려서, 폰에서는 관리자여도
              // "받아주기"가 아예 안 그려졌다. 두 곳이 같은 팝오버를 쓰는데
              // 넘기는 자리가 둘로 갈려 있으면 한쪽만 고치게 된다.
              onOpenAdmin={openAdmin}
              push={pushControl}
            />
          }
          filter={workspace.filter}
          rooms={workspace.rooms}
          loading={workspace.isLoadingRooms}
          selectedRoomId={selectedRoom?.id ?? null}
          query={workspace.query}
          onQueryChange={workspace.setQuery}
          // 방을 고르면 서랍은 할 일을 다 했다. 열어둔 채로 두면 고른 대화가
          // 그 뒤에 가려 있어서 눌린 건지 알 수 없다.
          onSelect={(roomId) => {
            setNavOpen(false);
            workspace.selectRoom(roomId);
          }}
          onOpenProfile={workspace.openProfile}
          onNewChat={workspace.openNewChat}
          onLoadMore={workspace.loadMoreRooms}
          footer={
            workspace.filter === "quiet" ? (
              <div className="flex items-center gap-2.5 border-t border-line px-5.5 py-4">
                <p className="flex-1 text-[13px] text-muted">
                  여기 있는 건 안 봐도 괜찮아요
                </p>
                <Button variant="ghost" size="sm" onClick={workspace.markAllQuietRead}>
                  모두 읽음
                </Button>
              </div>
            ) : workspace.undoNotice ? (
              <Snackbar
                className="m-3.5"
                message={workspace.undoNotice}
                actionLabel="닫기"
                onAction={workspace.dismissUndo}
              />
            ) : null
          }
          width={widths.list}
        />
      </NavDrawer>

      <ResizeHandle
        divider
        className="hidden md:flex"
        label="방 목록 폭"
        value={widths.list}
        min={limits.list.min}
        max={limits.list.max}
        onChange={(next) => setWidth("list", next)}
        onReset={() => resetWidth("list")}
      />

      {/* 대화는 늘 바탕에 있다. 좁은 화면에서는 서랍이 그 위를 덮을 뿐이다. */}
      <main className="flex min-w-0 flex-1 flex-col bg-paper">
        {/*
          방을 고르지 않았을 때의 ☰.

          서랍을 여는 단추는 원래 대화 헤더에 있는데, 그 헤더는 **방이 있을 때만**
          그려진다. 그래서 아무 대화도 안 연 채 서랍을 닫으면 빈 화면만 남고 다시
          열 방법이 사라졌다 — 가장자리 쓸기를 아는 사람만 빠져나올 수 있었다.
        */}
        {selectedRoom === null ? (
          <div className="border-line flex h-14 shrink-0 items-center border-b px-3 md:hidden">
            <IconButton label="방 목록 열기" onClick={() => setNavOpen(true)}>
              <Menu />
            </IconButton>
          </div>
        ) : null}

        {mainPane()}
      </main>

      <PeerProfileDialog
        room={workspace.profileRoom}
        open={workspace.profileOpen}
        onOpenChange={(open) => {
          if (!open) workspace.closeView();
        }}
        peer={workspace.profilePeer}
        sharedRooms={workspace.sharedRooms}
        // 프로필이 가리키는 방의 첨부여야 한다. 목록에서 연 프로필은 지금
        // 열려 있는 대화와 다른 방일 수 있다. 본문에 박힌 그림은 빼고 센다 —
        // 아래 "주고받은 파일"에 실제로 나오는 것과 숫자가 같아야 한다.
        attachmentCount={filesIn(workspace.profileMessages).length}
        onOpenRoom={workspace.openRoomFromProfile}
        onMoveToQuiet={workspace.toggleQuiet}
        onOpenFiles={workspace.openFiles}
        blocked={workspace.profilePeer?.blocked ?? false}
        onRename={workspace.openRenamePeer}
        onBlock={workspace.toggleBlockPeer}
      />

      <NewChatDialog
        open={workspace.newChatOpen}
        onOpenChange={(open) => {
          if (!open) workspace.closeView();
        }}
        contacts={contactsQuery.data?.items ?? []}
        // 보내는 것이 아니라 방을 여는 것이다. 발신 대기 상태를 물려주면
        // 다른 방에서 메일을 보내는 중일 때 이 단추가 같이 죽는다.
        pending={workspace.startChatPending}
        onStart={workspace.startChat}
      />

      <RoomFilesDialog
        open={workspace.filesOpen}
        onOpenChange={(open) => {
          if (!open) workspace.closeView();
        }}
        // 프로필과 같은 방을 본다. 열린 대화의 첨부가 아니다.
        messages={workspace.profileMessages}
        onDownload={downloadAttachment}
      />

      <RenameDialog
        open={workspace.renamePeerOpen}
        onOpenChange={(open) => {
          if (!open) workspace.closeView();
        }}
        title="이름 바꾸기"
        description="나에게만 보이는 이름이에요. 비우면 원래 이름으로 돌아가요."
        label="상대 이름"
        placeholder={workspace.profilePeer?.address ?? ""}
        initialValue={workspace.profilePeer?.displayName ?? ""}
        allowEmpty
        pending={workspace.profilePending}
        onSubmit={workspace.renamePeer}
      />

      <RenameDialog
        open={workspace.renameSelfOpen}
        onOpenChange={(open) => {
          if (!open) workspace.closeView();
        }}
        title="내 이름 바꾸기"
        description="메일을 보낼 때 상대에게 보이는 이름이에요."
        label="내 이름"
        initialValue={workspace.user?.displayName ?? ""}
        pending={workspace.profilePending}
        onSubmit={workspace.renameSelf}
      />

      <AvatarDialog
        open={workspace.avatarOpen}
        onOpenChange={(open) => {
          if (!open) workspace.closeView();
        }}
        user={workspace.user}
        pending={workspace.avatarPending}
        error={workspace.avatarError}
        onSubmit={workspace.changeAvatar}
        onRemove={workspace.removeAvatar}
      />

      <PasswordDialog
        open={workspace.passwordOpen}
        onOpenChange={(open) => {
          if (!open) workspace.closeView();
        }}
        pending={workspace.passwordPending}
        error={workspace.passwordError}
        onSubmit={workspace.changePassword}
      />

      <AddAddressDialog
        open={workspace.addAddressOpen}
        onOpenChange={(open) => {
          if (!open) workspace.closeView();
        }}
        // 도메인은 하나뿐이라 내 주소에서 떼어 쓴다. 따로 물어보면 요청만 늘고
        // 답이 늦게 오는 동안 `@…`가 잠깐 비어 보인다.
        mailDomain={workspace.user?.primaryAddress.split("@")[1] ?? ""}
        pending={workspace.addAddressPending}
        error={workspace.addAddressError}
        onSubmit={workspace.requestAddress}
      />

      <ManageAddressDialog
        address={workspace.managedAddress}
        onOpenChange={(open) => {
          if (!open) workspace.closeView();
        }}
        pending={workspace.addressPending}
        error={workspace.addressError}
        onSetEnabled={workspace.setAddressEnabled}
        onRemove={workspace.removeAddress}
      />
    </div>
  );
}
